import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { EffectTypes, FONT_SIZE_MAX, TRANSPARENCY_SIZE_MAX } from '@web/shared/components/web-hover-effects/types';
import type { FormGroup } from '@angular/forms';
import { type HoverEffectsFormGroup } from '@web/widgets/menu/type';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { NgFor, NgSwitch, NgSwitchCase } from '@angular/common';
import { SpInputNumComponent } from '@libs/components/sp-form-controls/sp-input-num/sp-input-num.component';
import { WebColorPickerComponent } from '../web-color-picker/web-color-picker.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'web-hover-effects',
    templateUrl: './web-hover-effects.component.html',
    styleUrls: ['./web-hover-effects.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgFor, NgSwitch, NgSwitchCase, SpInputNumComponent, WebColorPickerComponent, TranslateModule],
})
export class WebHoverEffectsComponent {
    @Input() effects: FormGroup<HoverEffectsFormGroup>;
    effectsOptions: { label: string; value: EffectTypes }[] = [
        {
            label: 'select_transparency',
            value: EffectTypes.transparency,
        },
        {
            label: 'select_change_color',
            value: EffectTypes.color,
        },
        {
            label: 'select_underline',
            value: EffectTypes.underline,
        },
        {
            label: 'select_animation',
            value: EffectTypes.animation,
        },
    ];

    underlineOptions = [
        { label: 'line_solid', value: 'solid' },
        { label: 'line_dashed', value: 'dashed' },
        { label: 'line_dotted', value: 'dotted' },
    ];
    animationTypes = [
        { label: 'animation_effect_up', value: 'moving_up' },
        { label: 'animation_effect_vibration', value: 'shaking' },
        { label: 'animation_effect_scale', value: 'scaling' },
    ];

    protected readonly EffectTypes = EffectTypes;
    protected readonly TRANSPARENCY_SIZE_MAX = TRANSPARENCY_SIZE_MAX;
    protected readonly FONT_SIZE_MAX = FONT_SIZE_MAX;

    setTransparencySize(value: number): void {
        this.transparency.patchValue(value);
    }

    setLineWidth(value: number): void {
        this.lineWidth.patchValue(value);
    }

    setColor({ value }: ColorPickerEventPayload): void {
        this.color.patchValue(value);
    }

    setType(event: Event): void {
        const value = Number((event.target as HTMLSelectElement).value) as EffectTypes;
        this.type.patchValue(value);
    }

    setUnderlineType(event: Event): void {
        const value = (event.target as HTMLSelectElement).value;
        this.lineType.patchValue(value);
    }

    setAnimationType(event: Event): void {
        const value = (event.target as HTMLSelectElement).value;
        this.animationType.patchValue(value);
    }

    public get type(): HoverEffectsFormGroup['type'] {
        return this.effects.get('type') as HoverEffectsFormGroup['type'];
    }

    public get transparency(): HoverEffectsFormGroup['transparency'] {
        return this.effects.get('transparency') as HoverEffectsFormGroup['transparency'];
    }

    public get color(): HoverEffectsFormGroup['color'] {
        return this.effects.get('color') as HoverEffectsFormGroup['color'];
    }

    public get columnWidth(): { select: number; value: number } {
        switch (this.type.value) {
            case EffectTypes.transparency:
                return { select: 8, value: 4 };
            case EffectTypes.color:
                return { select: 10, value: 2 };
            default:
                return { select: 12, value: 12 };
        }
    }

    public get lineType(): HoverEffectsFormGroup['lineType'] {
        return this.effects.get('lineType') as HoverEffectsFormGroup['lineType'];
    }

    public get lineWidth(): HoverEffectsFormGroup['lineWidth'] {
        return this.effects.get('lineWidth') as HoverEffectsFormGroup['lineWidth'];
    }

    public get animationType(): HoverEffectsFormGroup['animationType'] {
        return this.effects.get('animationType') as HoverEffectsFormGroup['animationType'];
    }
}
